import { Redirect, router } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { BackChevron } from '@/components/BackChevron';
import { Button } from '@/components/Button';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { GiftProductCard } from '@/components/gifting/GiftProductCard';
import { colors, radii, spacing } from '@/constants/theme';
import { GIFTING_ROUTES, giftingRouteFor } from '@/lib/giftingRoute';
import { authStore } from '@/stores/auth.store';
import { giftingStore } from '@/stores/gifting.store';
import type { GiftProduct } from '@/types/gifting';

/**
 * The 2-col gift grid. It decides nothing about eligibility: the server's
 * `gifting_next_step` says which screen this user belongs on, and we render
 * only if that answer is us. Everything else is a redirect to wherever it
 * pointed — claimed users to their gift, ineligible ones to the landing.
 *
 * This screen used to re-derive that from `gift_claim` + `gifting_enabled`
 * while the index routed on `gifting_next_step`. Two sources for one answer,
 * and when they disagreed the two screens bounced the user between each other
 * forever. One source now.
 *
 * Sold-out is still local: all products gone → page-level banner, because ten
 * silently-dimmed tiles read as a broken screen.
 */
const ChooseGiftScreen = observer(() => {
  const { products, loaded, loadError, allSoldOut } = giftingStore;

  useEffect(() => {
    void giftingStore.ensureLoaded();
    // The step in the cached payload can go stale mid-session — most notably
    // when GIFTING_ENABLED flips (teardown is a scheduled event, not an edge
    // case). Refreshing on mount means the grid re-checks with the server
    // instead of trusting an answer from before the flip. No-ops when logged
    // out; the redirect guards below re-run when the fresh payload lands.
    void authStore.refreshMe();
  }, []);

  // Same pre-hydration spinner as index/complete: never flash the grid at a
  // session we haven't identified yet.
  if (!authStore.hydrated) {
    return (
      <View style={styles.hydrating}>
        <ActivityIndicator color={colors.deepGreen} />
      </View>
    );
  }

  // Logged-out deep links (share URLs land people here) go to the landing for
  // the pitch, not the phone form — they haven't read Jason's email. Handled
  // separately because there's no user for the server to have an opinion on.
  if (!authStore.user) {
    return <Redirect href={GIFTING_ROUTES.landing} />;
  }

  const step = giftingRouteFor(authStore.user);
  if (step !== GIFTING_ROUTES.choose) {
    return <Redirect href={step ?? GIFTING_ROUTES.landing} />;
  }

  function onSelect(product: GiftProduct) {
    router.push({ pathname: '/season-of-gifting/product/[id]', params: { id: String(product.id) } });
  }

  const rows: GiftProduct[][] = [];
  for (let i = 0; i < products.length; i += 2) {
    rows.push(products.slice(i, i + 2));
  }

  return (
    <Screen width="wide">
      {/* Explicit target: after a PIN redirect the auth screens sit under
          this route, so a plain goBack() would pop into signup/login and
          bounce straight back — a visible no-op (review finding, same class
          as the intro fix). */}
      <BackChevron onPress={() => router.replace('/(tabs)')} />
      <View style={styles.body}>
        <Heading size="4xl">
          Pick your free gift
        </Heading>
        <Text color="textMuted" style={styles.copy}>
          Choose one product you&apos;d genuinely be excited to try.{'\n\n'}This is your
          introduction to the brand. Try the product, see what you think, and if you love it,
          this could be the start of a longer-term NIL partnership.
        </Text>

        {allSoldOut && (
          <View style={styles.allClaimedBanner}>
            <Text variant="bodyMedium">All gifts have been claimed 🎁</Text>
            <Text variant="caption" color="textMuted" style={styles.allClaimedCopy}>
              That was fast. There are still paid Rewards waiting for you.
            </Text>
            <Button
              label="See ways to earn"
              variant="outline"
              size="sm"
              onPress={() => router.push('/(tabs)/deals')}
              style={styles.allClaimedCta}
            />
          </View>
        )}

        {loadError ? (
          <ErrorState
            title="Couldn't load the gifts"
            body="Check your connection and try again."
            onRetry={() => void giftingStore.load()}
          />
        ) : (
          <View style={styles.grid}>
            {rows.map((row, i) => (
              <View key={i} style={styles.row}>
                {row.map((product) => (
                  <GiftProductCard key={product.id} product={product} onPress={onSelect} />
                ))}
                {row.length === 1 && <View style={styles.spacer} />}
              </View>
            ))}
            {/* `!loaded` alone (not `!loaded && loading`): before hydration and
                at static-export time no effect has run yet, so `loading` is
                false — the old condition rendered an EMPTY grid as the web
                first paint (review finding). */}
            {!loaded && products.length === 0 && (
              <Text color="textMuted" align="center" style={styles.loadingText}>
                Loading gifts…
              </Text>
            )}
          </View>
        )}
      </View>
    </Screen>
  );
});

export default ChooseGiftScreen;

const styles = StyleSheet.create({
  body: {
    paddingTop: spacing.sm,
  },
  copy: {
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  grid: {
    gap: spacing.md,
  },
  row: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  spacer: {
    flex: 1,
  },
  hydrating: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  loadingText: {
    paddingVertical: spacing.xl,
  },
  allClaimedBanner: {
    backgroundColor: colors.brightGreenSoft,
    borderRadius: radii.lg,
    padding: spacing.base,
    marginBottom: spacing.lg,
  },
  allClaimedCopy: {
    marginTop: spacing.xs,
  },
  allClaimedCta: {
    marginTop: spacing.md,
    alignSelf: 'flex-start',
  },
});
